import type { DraftType } from '@repo/common';
import type { Section } from '@repo/common/types/FilterSet';
import { formatFullDate } from '@repo/toolslib/src/utils/component.util';
import type { Readable } from 'svelte/store';
import { FilteredType } from './base';

export interface DriveItemType {
	id: string;
	name: string;
	type: DraftType;
	lastModified: Date;
	workspaceId?: string;
	isFavorite?: boolean;
	body?: string;
	title?: string;
	date?: Date;
}

export interface MappedDriveItem {
	id: string;
	title: string;
	type: DraftType;
	lastModified: Date;
	workspaceId?: string;
	fullDate: string;
}

// Concrete implementation for Drive
export class FilteredDrive extends FilteredType<DriveItemType, MappedDriveItem> {
	private lastItems: DriveItemType[] | null = null;
	private lastSortedItems: DriveItemType[] | null = null;
	private lastItemsLength: number = 0;

	constructor(items: Readable<DriveItemType[]>, initialLimit: number = 20) {
		super(items, initialLimit);

		// Drive items should show all items by default
		this.filterDatePresetValues.set([]);
	}

	protected sortItems(items: DriveItemType[]): DriveItemType[] {
		// Check if we can use cached sorted items
		if (this.lastItems === items && this.lastSortedItems && this.lastItemsLength === items.length) {
			return this.lastSortedItems;
		}

		// Sort by lastModified date (newest first)
		const sorted = [...items].sort((a, b) => {
			const dateA = a.lastModified?.getTime() || 0;
			const dateB = b.lastModified?.getTime() || 0;
			return dateB - dateA;
		});

		// Update cache
		this.lastItems = items;
		this.lastSortedItems = sorted;
		this.lastItemsLength = items.length;

		return sorted;
	}

	// Map raw item to UI item
	protected mapItem(item: DriveItemType): MappedDriveItem {
		return {
			id: item.id,
			title: item.name || item.title || 'Untitled',
			type: item.type,
			lastModified: item.lastModified || new Date(),
			workspaceId: item.workspaceId,
			fullDate: formatFullDate(item.lastModified || new Date())
		};
	}

	// Generate sections from mapped items
	protected generateSections(items: MappedDriveItem[]): Section<MappedDriveItem>[] {
		const sections = new Map<string, MappedDriveItem[]>();

		for (const item of items) {
			const dateKey = item.fullDate;

			if (!sections.has(dateKey)) {
				sections.set(dateKey, []);
			}
			sections.get(dateKey)!.push(item);
		}

		// Convert to Section format and sort by date (newest first)
		return Array.from(sections.entries())
			.sort(([dateA], [dateB]) => {
				// Parse dates and compare
				const a = new Date(dateA).getTime();
				const b = new Date(dateB).getTime();
				return b - a;
			})
			.map(([date, sectionItems]) => ({
				title: date,
				items: sectionItems
			}));
	}

	// Generate sections from raw items (for lazy mapping optimization)
	public generateSectionsFromRawItems(items: DriveItemType[]): Section<DriveItemType>[] {
		const sections = new Map<string, DriveItemType[]>();

		for (const item of items) {
			const dateKey = formatFullDate(item.lastModified || new Date());

			if (!sections.has(dateKey)) {
				sections.set(dateKey, []);
			}
			sections.get(dateKey)!.push(item);
		}

		// Convert to Section format and sort by date (newest first)
		return Array.from(sections.entries())
			.sort(([dateA], [dateB]) => {
				const a = new Date(dateA).getTime();
				const b = new Date(dateB).getTime();
				return b - a;
			})
			.map(([date, sectionItems]) => ({
				title: date,
				items: sectionItems
			}));
	}

	// Required abstract method implementations
	protected getItemTags(_item: DriveItemType): string[] {
		// Drive items don't have tags yet
		return [];
	}

	protected getItemSpace(item: DriveItemType): string | undefined {
		return item.workspaceId;
	}

	protected getItemText(item: DriveItemType): string {
		const title = item.name || item.title || '';
		const body = item.body || '';
		return `${title} ${body}`.trim();
	}

	protected getItemCalendar(_item: DriveItemType): string | undefined {
		// Drive items don't have calendars
		return undefined;
	}

	protected getItemMemory(_item: DriveItemType): string | undefined {
		// Drive items don't have memories
		return undefined;
	}
}
